import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, ArrowLeft, AlertCircle, HandCoins } from "lucide-react";
import { COMPANY, SQUARE_PAYMENT_URL } from "@/lib/data";
import { trackConversion } from "@/lib/weeklySpecialTracking";
import CardPaymentForm from "@/components/CardPaymentForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PayPage() {
  const [params] = useSearchParams();
  const bookingId = params.get("b");
  const sessionId = params.get("session_id");
  const setupSession = params.get("setup_session");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payStatus, setPayStatus] = useState(sessionId ? "checking" : null);
  const [cardSaved, setCardSaved] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!bookingId) { setError("Missing booking reference."); setLoading(false); return; }
    axios.get(`${API}/payments/booking-summary/${bookingId}`)
      .then((r) => setSummary(r.data))
      .catch(() => setError("We couldn't find that booking."))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const poll = useCallback(async (attempt = 0) => {
    if (attempt >= 6) { setPayStatus("timeout"); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPayStatus("paid");
        trackConversion("Purchase", data.amount_total ? data.amount_total / 100 : null);
        return;
      }
      if (data.status === "expired") { setPayStatus("expired"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { if (sessionId) poll(0); }, [sessionId, poll]);

  useEffect(() => {
    if (!setupSession) return;
    axios.get(`${API}/payments/setup-status/${setupSession}`)
      .then(({ data }) => setCardSaved(data.saved ? { brand: data.brand, last4: data.last4 } : false))
      .catch(() => setCardSaved(false));
  }, [setupSession]);

  const onPaid = (data) => {
    setPayStatus("paid");
    trackConversion("Purchase", data.total_charged || null);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-[#F2B705] text-sm mb-6 transition-colors"><ArrowLeft size={16} /> Back to site</Link>
        <div className="flex items-center gap-3">
          <img src={COMPANY.logo} alt="Auto-Hire STL" className="h-14 w-14 object-contain rounded-md" />
          <div className="font-display font-extrabold text-2xl">AutoHire <span className="text-gold-gradient">STL</span></div>
        </div>

        <div className="mt-8 bg-[#0C0C0C] border border-white/10 rounded-2xl p-7" data-testid="pay-card">
          {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#F2B705]" /></div>
          : payStatus === "paid" ? (
            <div className="text-center py-6" data-testid="pay-success">
              <CheckCircle2 className="text-[#F2B705] mx-auto" size={48} />
              <h1 className="font-display font-black text-2xl mt-4">Payment Received! 🎉</h1>
              <p className="text-white/60 mt-2">Thank you{summary?.name ? `, ${summary.name}` : ""}! Your payment is confirmed. We'll be in touch shortly.</p>
            </div>
          ) : error ? (
            <div className="text-center py-6" data-testid="pay-error"><AlertCircle className="text-red-400 mx-auto" size={40} /><p className="text-white/70 mt-3">{error}</p></div>
          ) : (
            <>
              {setupSession && cardSaved && (
                <div data-testid="card-saved-banner" className="mb-5 rounded-lg border border-green-500/30 bg-green-500/[0.07] p-4 flex items-center gap-3">
                  <CheckCircle2 className="text-green-400 shrink-0" size={22} />
                  <p className="text-sm text-white/80">Card {cardSaved.brand ? `${cardSaved.brand} ` : ""}ending <b className="text-white">{cardSaved.last4}</b> is now securely on file. Thank you!</p>
                </div>
              )}
              {setupSession && cardSaved === false && (
                <div data-testid="card-saved-failed" className="mb-5 rounded-lg border border-white/15 bg-[#0A0A0A] p-4 text-sm text-white/60">
                  We couldn't confirm the saved card yet. If you completed it, it will appear on your account shortly.
                </div>
              )}
              <h1 className="font-display font-black text-3xl">Make a Payment</h1>
              {summary && (
                <div className="mt-5 space-y-2 text-sm">
                  <Row label="Name" value={summary.name} />
                  <Row label="Vehicle" value={summary.vehicle} />
                  <Row label="Rental" value={`${summary.pickup_date} → ${summary.return_date}`} />
                  <Row label="Total" value={money(summary.total_price)} />
                  <Row label="Paid" value={money(summary.amount_paid)} />
                  <div className="flex justify-between pt-3 mt-2 border-t border-white/10">
                    <span className="text-white/60">Balance Due</span>
                    <span data-testid="pay-balance" className="font-display font-black text-2xl text-[#F2B705]">{money(summary.balance_due)}</span>
                  </div>
                  {summary.card_fee && (
                    <div data-testid="pay-card-fee-disclosure" className="mt-3 rounded-lg border border-white/15 bg-white/[0.04] p-3 space-y-1">
                      <Row label="Subtotal" value={money(summary.card_fee.amount_due)} />
                      <Row label={`Processing Fee (${summary.card_fee.percent}% + $${(summary.card_fee.fixed_cents / 100).toFixed(2)}, credit only — estimate)`} value={money(summary.card_fee.processing_fee)} />
                      <div className="flex items-center justify-between pt-1 border-t border-white/10">
                        <span className="text-white/80 text-sm font-bold">Total (estimate)</span>
                        <span data-testid="pay-total-charged" className="font-bold text-white">{money(summary.card_fee.total_charged)}</span>
                      </div>
                      <p className="text-[10px] text-white/40 pt-1">{summary.card_fee.disclosure} The exact fee for your card is shown below once entered.</p>
                    </div>
                  )}
                  {summary.card_fee && summary.balance_due > 0 && (
                    <div data-testid="pay-fee-free-nudge" className="mt-2 rounded-lg border border-green-400/25 bg-green-400/[0.06] p-3 flex items-start gap-2.5">
                      <HandCoins size={16} className="text-green-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-green-200/85 leading-relaxed">
                        <span className="font-bold text-green-300">Skip the card fee:</span> pay your balance of{" "}
                        <span className="font-bold text-white">{money(summary.balance_due)}</span> with{" "}
                        <span className="font-semibold">Cash, Zelle, or Cash App</span> — no processing fee applies.
                        Ask us at pickup or reply to your confirmation text to arrange it.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {payStatus === "checking" && <p className="text-[#F2B705] text-sm mt-4 flex items-center gap-2"><Loader2 className="animate-spin" size={15} /> Confirming your payment...</p>}
              {(payStatus === "timeout" || payStatus === "expired") && <p className="text-white/60 text-sm mt-4">We couldn't confirm payment yet. If you completed it, you'll get a confirmation shortly.</p>}

              {summary?.balance_due > 0 && payStatus !== "checking" ? (
                <div className="mt-6 space-y-3">
                  <CardPaymentForm bookingId={bookingId} balanceDue={summary.balance_due} onPaid={onPaid} />
                  {SQUARE_PAYMENT_URL && (
                    <a href={SQUARE_PAYMENT_URL} target="_blank" rel="noreferrer" data-testid="pay-square-btn"
                      className="w-full block text-center border border-white/15 font-bold py-3.5 rounded-md hover:bg-white hover:text-black transition-colors">
                      Pay with Square
                    </a>
                  )}
                  <p className="text-white/35 text-xs text-center">Secure payment powered by Stripe. Your card details never touch our servers.</p>
                </div>
              ) : payStatus !== "checking" && (
                <p className="text-green-400 mt-6 text-center font-semibold">✓ This booking is fully paid. Thank you!</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-white/45">{label}</span><span className="text-white/85">{value}</span></div>
);
